import React, { useState } from 'react';

/**
 * DateRangeFilter component for dashboard analytics
 * @param {Object} props - Component props
 * @param {Function} props.onDateRangeChange - Callback when date range changes
 * @param {Object} props.currentRange - Current date range { startDate, endDate }
 * @returns {JSX.Element} DateRangeFilter component
 */
const DateRangeFilter = ({
  onDateRangeChange,
  currentRange: _currentRange,
}) => {
  const [selectedPreset, setSelectedPreset] = useState('7days');
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [error, setError] = useState('');

  const getDateRange = preset => {
    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);
    const startDate = new Date();

    switch (preset) {
      case '7days':
        startDate.setDate(endDate.getDate() - 7);
        break;
      case '30days':
        startDate.setDate(endDate.getDate() - 30);
        break;
      case '3months':
        startDate.setMonth(endDate.getMonth() - 3);
        break;
      case 'custom':
        return null; // Custom range handled separately
      default:
        startDate.setDate(endDate.getDate() - 7);
    }

    startDate.setHours(0, 0, 0, 0);
    return { startDate, endDate };
  };

  const handlePresetChange = preset => {
    setSelectedPreset(preset);
    setShowCustomRange(preset === 'custom');

    if (preset !== 'custom') {
      const range = getDateRange(preset);
      if (range) {
        onDateRangeChange({
          startDate: range.startDate.toISOString(),
          endDate: range.endDate.toISOString(),
        });
      }
    }
  };

  const handleCustomRangeApply = () => {
    setError('');
    if (customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);

      if (start <= end) {
        onDateRangeChange({
          startDate: start.toISOString(),
          endDate: end.toISOString(),
        });
        setSelectedPreset('custom');
      } else {
        setError('Start date must be before or equal to end date');
      }
    } else {
      setError('Please select both start and end dates');
    }
  };

  const presets = [
    { value: '7days', label: 'Last 7 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: '3months', label: 'Last 3 Months' },
    { value: 'custom', label: 'Custom Range' },
  ];

  return (
    <div className="flex items-center space-x-2 flex-wrap gap-2">
      {presets.map(preset => (
        <button
          key={preset.value}
          onClick={() => handlePresetChange(preset.value)}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            selectedPreset === preset.value
              ? 'bg-primary-600 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          {preset.label}
        </button>
      ))}

      {showCustomRange && (
        <div className="flex flex-col space-y-2">
          <div className="flex items-center space-x-2 bg-white p-2 rounded-md border border-gray-300">
            <input
              type="date"
              value={customStartDate}
              onChange={e => {
                setCustomStartDate(e.target.value);
                setError('');
              }}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Start Date"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={customEndDate}
              onChange={e => {
                setCustomEndDate(e.target.value);
                setError('');
              }}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="End Date"
            />
            <button
              onClick={handleCustomRangeApply}
              className="px-4 py-1 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              Apply
            </button>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
